import { UiRegionItem } from 'utils/cfUiItems/base';
import { isUiRegionItem } from 'utils/cfUiItems/guards';
import { isBboxShapeProps, isMaskShapeProps, isPolygonShapeProps } from 'utils/typeguards/predictionOutputs';
import { MaskConfig, PolygonConfig, RectConfig } from 'modules/Canvas/helpers/types';
import {
  convertPositivePointToPointCanvasConfig,
  convertUIRegionItemToPointCanvasConfig,
  isPointShapeProps,
  PointCanvasConfig,
} from 'utils/annotations/point';

export function uiItemToShapeConfig(media: HTMLImageElement | HTMLVideoElement) {
  return (item: UiRegionItem): RectConfig | PolygonConfig | MaskConfig | PointCanvasConfig | null => {
    if (!isUiRegionItem(item)) return null;
    if (isPointShapeProps(item.regionData)) {
      if (typeof item.metadata?.positive === 'boolean') {
        return convertPositivePointToPointCanvasConfig({ item, media, positive: item.metadata.positive });
      }
      return convertUIRegionItemToPointCanvasConfig(item, media);
    } else if (isPolygonShapeProps(item.regionData)) {
      const { regionData, key } = item;
      const { color, polygon } = regionData;
      const concept = item.concepts?.[0];
      const points = getPolygonProps(polygon, media);

      return {
        points,
        stroke: color,
        key,
        text: 'concepts' in item && item.concepts ? item.concepts[0]?.name : '',
        concept,
      } as PolygonConfig;
    } else if (isBboxShapeProps(item.regionData)) {
      const { regionData, key } = item as unknown as UiRegionItem;
      const { color, bbox } = regionData as CF.API.BboxShapeProps;
      const concept = item.concepts?.[0];

      const { x, y, width, height } = getRectangleProps(bbox, media);

      return {
        x,
        y,
        width,
        height,
        color,
        key,
        text: concept ? concept.name || concept.id : 'N/A',
        concept: concept || { id: 'N/A', name: 'N/A' },
      } as RectConfig;
    } else if (isMaskShapeProps(item.regionData) && (item.regionData as CF.API.MaskShapeProps)?.mask) {
      const {
        key,
        regionData: {
          color,
          mask: {
            image: { base64 = '' },
          },
        },
      } = item;
      const concept = item.concepts?.[0];
      return concept
        ? ({
            key,
            text: concept.name || concept.id,
            base64,
            color,
            concept,
          } as MaskConfig)
        : null;
    }

    return null;
  };
}

function getRectangleProps(
  bbox: CF.API.BoundingBox,
  media: HTMLImageElement | HTMLVideoElement,
): { x: number; y: number; width: number; height: number } {
  const { naturalHeight, naturalWidth, videoHeight, videoWidth } = media as {
    naturalHeight?: number;
    naturalWidth?: number;
    videoHeight?: number;
    videoWidth?: number;
  };
  const height = naturalHeight || videoHeight || media.height;
  const width = naturalWidth || videoWidth || media.width;

  const x = bbox.left_col * width;
  const y = bbox.top_row * height;

  return {
    x,
    y,
    width: bbox.right_col * width - x,
    height: bbox.bottom_row * height - y,
  };
}

function getPolygonProps(polygon: CF.API.Polygon, media: MediaDimensions): PolygonConfig['points'] {
  const { naturalHeight, naturalWidth, videoHeight, videoWidth } = media;
  const height = naturalHeight || videoHeight || 0;
  const width = naturalWidth || videoWidth || 0;

  return polygon.points.map(({ row, col }) => [col * width, row * height]);
}

interface MediaDimensions {
  naturalHeight?: number;
  naturalWidth?: number;
  videoHeight?: number;
  videoWidth?: number;
}

/**
 * Calculates the area of a polygon using the Shoelace formula (Gauss's area formula).
 * The polygon is defined by an array of points, each with `col` (x-coordinate) and `row` (y-coordinate).
 *  https://rosettacode.org/wiki/Shoelace_formula_for_polygonal_area
 *  https://en.wikipedia.org/wiki/Shoelace_formula
 * @param polygon - The polygon object containing an array of points.
 * @returns The calculated area of the polygon.
 */
export function calculatePolygonArea(polygon: CF.API.Polygon): number {
  const points = polygon.points;
  let area = 0;
  const numPoints = points.length;
  for (let i = 0; i < numPoints; i += 1) {
    const nextIndex = (i + 1) % numPoints;
    // This term is (x_i * y_(i+1))
    area += points[i].col * points[nextIndex].row;
    // This term is (y_i * x_(i+1))
    area -= points[nextIndex].col * points[i].row;
  }
  return Math.abs(area / 2);
}
